import type {
  Line,
  LineSegments,
  Material,
  Mesh,
  Object3D,
  Plane,
  Points,
} from 'three';

import { PARTVIEWER_SHARED_MATERIAL_POOL } from './cross-section-utils';

const CS_MESH_ACTIVE = '_partViewerCrossSectionMeshActive';
const CS_ORIG_MATERIAL = '_partViewerCrossSectionOrigMaterial';

type Drawable = Mesh | LineSegments | Line | Points;

function isDrawable(o: Object3D): o is Drawable {
  const anyO = o as Mesh & LineSegments & Line & Points;
  return Boolean(
    anyO.isMesh || anyO.isLineSegments || anyO.isLine || anyO.isPoints,
  );
}

function asMaterialArray(m: Material | Material[]): Material[] {
  return Array.isArray(m) ? m : [m];
}

function setMaterialArray(obj: Drawable, mats: Material[]): void {
  obj.material = mats.length === 1 ? mats[0] : mats;
}

/**
 * Apply clipping plane to all drawables under `root` using one shared Plane instance.
 * Materials from the global PMI pool are cloned per mesh so other viewers are unaffected.
 */
export function applyCrossSectionToObjectSubtree(
  root: Object3D,
  plane: Plane,
): void {
  root.traverse((obj) => {
    if (!isDrawable(obj)) return;
    if (obj.userData[CS_MESH_ACTIVE]) return;

    const orig = obj.material;
    obj.userData[CS_ORIG_MATERIAL] = orig;
    const origMats = asMaterialArray(orig);
    const newMats = origMats.map((mat) => {
      if (mat.userData?.[PARTVIEWER_SHARED_MATERIAL_POOL]) {
        const c = mat.clone();
        c.clipping = true;
        c.clippingPlanes = [plane];
        return c;
      }
      mat.clipping = true;
      mat.clippingPlanes = [plane];
      return mat;
    });
    setMaterialArray(obj, newMats);
    obj.userData[CS_MESH_ACTIVE] = true;
  });
}

export function clearCrossSectionFromObjectSubtree(root: Object3D): void {
  root.traverse((obj) => {
    if (!isDrawable(obj)) return;
    if (!obj.userData[CS_MESH_ACTIVE]) return;

    const orig = obj.userData[CS_ORIG_MATERIAL] as
      | Material
      | Material[]
      | undefined;
    const origMats = orig !== undefined ? asMaterialArray(orig) : [];
    const currentMats = asMaterialArray(obj.material);

    for (let i = 0; i < currentMats.length; i++) {
      const cur = currentMats[i];
      const origMat = origMats[i];
      if (origMat !== undefined && cur === origMat) {
        cur.clipping = false;
        cur.clippingPlanes = null;
      } else {
        cur.dispose();
      }
    }

    if (orig !== undefined) {
      obj.material = orig;
    }

    delete obj.userData[CS_ORIG_MATERIAL];
    delete obj.userData[CS_MESH_ACTIVE];
  });
}
